import React from 'react';
import { useTranslation } from 'react-i18next';

import LockedReward from '../../../../assets/images/LockedReward.png';
import ButtonWithIcon from '../../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import baseServerUrl from '../../../../services/apiServer';

import styles from './SmartRewardBadge.module.scss';

/**
 * SmartRewardBadge — displays reward visibility info based on the
 * `reward_visibility` object returned by the backend.
 *
 * Visibility modes:
 *  - "full"          → exact reward amount (logged-in eligible user, deterministic)
 *  - "starting_from" → minimum reward with "Starting from" label (candidate-dependent)
 *  - "up_to"         → max reward with "Up to" label + login CTA (anonymous, internal-only)
 *  - "not_eligible"  → max reward with "Up to" label, no CTA (logged-in, not eligible)
 *  - "none"          → nothing rendered
 */
const SmartRewardBadge = ({ rewardVisibility }) => {
  const { t } = useTranslation('common');

  if (!rewardVisibility || rewardVisibility.visibility === 'none') {
    return null;
  }

  const { visibility, display_label, internal_only, requires_login } = rewardVisibility;

  // For "full" visibility, we don't show this badge — the existing
  // ReferralRewardBadge / OpeningReferralRewards handles the full detail view.
  if (visibility === 'full') {
    return null;
  }

  const getHeading = () => {
    if (visibility === 'up_to' || visibility === 'not_eligible') {
      return display_label;
    }
    if (visibility === 'starting_from') {
      return display_label;
    }
    return display_label;
  };

  const getSubtext = () => {
    if (visibility === 'up_to' && requires_login) {
      return t('rewards.loginToSeeReward');
    }
    if (visibility === 'not_eligible' && internal_only) {
      return t('rewards.internalOnly');
    }
    if (visibility === 'starting_from') {
      return t('rewards.startingFromDescription');
    }
    return null;
  };

  const showLoginButton = visibility === 'up_to' && requires_login;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.rewardContainer}>
        <img src={LockedReward} alt="reward" className={styles.rewardIcon} />
        <div className={styles.detailContainer}>
          <span className={styles.rewardHeading}>{getHeading()}</span>
          {getSubtext() && (
            <span className={styles.rewardText}>{getSubtext()}</span>
          )}
        </div>
      </div>
      {showLoginButton && (
        <ButtonWithIcon
          text={t('buttons.signupNow')}
          className={styles.buttonSignUp}
          textClassName="btn-icon-text-primary-40"
          variant="primary-36"
          onClick={() => {
            window.location.href = `${baseServerUrl}/login`;
          }}
        />
      )}
    </div>
  );
};

export default SmartRewardBadge;
